import { useAuth } from "@/contexts/AuthContext";
import { tierBreakdown } from "@/lib/phoenixTokens";

/**
 * Compact Phoenix Token balance display.
 * Variants:
 *  - "compact"  (default): total + small flame icon, fits in header
 *  - "full":               total + tier-by-tier breakdown grid
 */
export default function TokenBalance({ variant = "compact", className = "" }) {
  const { user } = useAuth();
  if (!user || user === false) return null;

  const total = user.tokens || 0;
  const breakdown = user.tokens_breakdown || tierBreakdown(total);

  if (variant === "compact") {
    return (
      <span
        data-testid="token-balance-compact"
        className={`inline-flex items-center gap-1.5 font-mono tabular-nums font-bold text-[#D4AF37] text-xs ${className}`}
        title={`${total} Phoenix Tokens`}
      >
        <span className="text-base leading-none">🐦‍🔥</span>
        <span>{total.toLocaleString()}</span>
      </span>
    );
  }

  // FULL variant
  return (
    <div
      data-testid="token-balance-full"
      className={`bg-[#0F0F0F] border border-zinc-800 p-6 mt-6 ${className}`}
    >
      <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold inline-flex items-center gap-1.5">
            <span className="text-sm">🐦‍🔥</span> Phoenix Tokens
          </div>
          <h2 className="font-display text-3xl text-white">
            Rise of the <span className="text-[#D4AF37]">Phoenix.</span>
          </h2>
        </div>
        <div
          data-testid="token-total"
          className="font-mono tabular-nums font-bold text-4xl text-[#D4AF37] leading-none"
        >
          {total.toLocaleString()}
          <span className="text-xs text-neutral-500 ml-2">Phoenix Tokens</span>
        </div>
      </div>

      {breakdown.length === 0 ? (
        <p className="text-sm text-neutral-500 text-center py-6">
          Earn your first token — sign in tomorrow for 5 ✨ Sparks, log a workout for a 🔥 Flame.
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {breakdown.map((t) => (
            <div
              key={t.key}
              data-testid={`token-tier-${t.key}`}
              className="bg-[#0A0A0A] border border-zinc-900 hover:border-[#D4AF37] transition-colors p-3 text-center"
              title={`${t.count} × ${t.name} (${t.value} Phoenix Tokens each)`}
            >
              <div className="text-2xl">{t.icon}</div>
              <div className="font-mono tabular-nums font-bold text-[#D4AF37] text-lg leading-tight">
                ×{t.count}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-neutral-500 truncate">
                {t.name}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-[10px] text-neutral-600 uppercase tracking-widest mt-4 text-center">
        Earn tokens by signing in, logging activity, unlocking badges & rising together.
      </p>
    </div>
  );
}
